'use client'
import { cn } from '@mntn-dev/ui-utilities'
import { motion } from 'motion/react'
import type { PropsWithChildren, ReactNode } from 'react'

import {
  elevationMap,
  themeBackgroundMap,
  themeBorderColorMap,
  themeHoverGlowMap,
} from '@mntn-dev/ui-theme'

import { getTestProps } from '../../utils/index.ts'
import {
  CardProvider,
  type CardScale,
  type UseCardProps,
  useCard,
  useCardContext,
} from './use-card.ts'

export type CardCompactProps = Readonly<
  UseCardProps & {
    as?: 'div' | 'li'
    children: ReactNode
    className?: string
    onClick?: () => void
    orientation?: 'horizontal' | 'vertical'
  }
>

const paddingMap: Record<CardScale, string> = {
  sm: 'p-1',
  md: 'p-1.5',
  lg: 'p-2',
}

const gapMap: Record<CardScale, string> = {
  sm: 'gap-1',
  md: 'gap-1.5',
  lg: 'gap-2',
}

export const CardCompact = ({
  as,
  children,
  className,
  onClick,
  ...useCardProps
}: CardCompactProps) => {
  const Component = as ?? 'div'
  const context = useCard(useCardProps)

  const { dataTestId, dataTrackingId, scale = 'lg' } = context

  return (
    <CardProvider value={context}>
      <Component
        className={cn(
          'flex rounded-lg text-left',
          themeBackgroundMap['container-secondary'],
          themeBorderColorMap.muted,
          elevationMap.xs,
          paddingMap[scale],
          onClick && cn('cursor-pointer', themeHoverGlowMap.info),
          className
        )}
        onClick={onClick}
        {...getTestProps({ dataTestId, dataTrackingId })}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="w-full"
        >
          {children}
        </motion.div>
      </Component>
    </CardProvider>
  )
}

export const CardCompactContent = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => {
  const { orientation, scale = 'lg' } = useCardContext()
  return (
    <div
      className={cn(
        'w-full flex',
        orientation === 'vertical' && 'flex-col',
        gapMap[scale],
        className
      )}
    >
      {children}
    </div>
  )
}
